import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField, Autocomplete, Chip, Avatar, Box, CircularProgress, FormControl, Select, MenuItem, InputAdornment } from "@mui/material";
import "./style.css";
import TicketSubject from "./TicketSubject";
import TicketDropdowns from "./TicketDropdowns";
import TicketTagInput from "./TicketTagInput";
import CreateTicketMessageContainer from "./CreateTicketMessageContainer";
import TicketDatePicker from "./TicketDatePicker";
import TicketAssignTeam from "./TicketAssignTeam";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import { createTicket } from "../../../../store/features/createTicket";
import { toast, ToastContainer } from "react-toastify";
import Input from "../../../common/InputField";
import { listAccessibleGroupsAPI } from "../../../../store/features/listGroup";
import TeamMemberSelect from "../TicketMainSection/TeamMemberSelect";
import { ListCoRoleName } from "../../../../store/features/ListCoUserByRole";
import { ReactComponent as Cancel } from "../../../../assets/images/TicketImages/cancel.svg";
import { ReactComponent as SearchIcon } from '../../../../assets/images/TicketImages/search.svg';
import TicketGroupSelector from "../TicketMainSection/TicketGroupSelector";
import { useSocket } from "../../../../SocketContext";
function validateForm({ subject, status, priority, customerEmail, customerName, assignee, tags, groups }) {
  const errors = [];

  if (!subject.trim()) errors.push("Subject is required.");
  if (!customerName.trim()) errors.push("Customer name is required.");
  if (customerName.length > 50) errors.push("Customer name cannot exceed 50 characters.");
  if (!customerEmail.trim()) errors.push("Customer email is required.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) errors.push("Invalid email format.");
  if (!groups || groups.length === 0) errors.push("Group is required.");
  if (!assignee) errors.push("Assigned agent is required.");

  if (!priority.trim()) errors.push("Priority is required.");
  if (!status.trim()) errors.push("Status is required.");
  if (tags.length === 0) errors.push("At least one tag is required.");

  return errors;
}

function CreateTicket({ onCancel, onCreated, embedded = false }) {
  const dispatch = useDispatch();
  const socket = useSocket();
  const [subject, setSubject] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [assignedAgent, setAssignedAgent] = useState("");
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState("");
  const userData = useSelector((state) => state.loginToken);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupSearchTerm, setGroupSearchTerm] = useState("");

  const [teamMembers, setTeamMembers] = useState([]);
  const selectedGroupId = groups[0] || "";

  useEffect(() => {
    if (!selectedGroupId) {
      setTeamMembers([]);
      setAssignedAgent("");
      return;
    }

    let cancelled = false;
    (async () => {
      const response = await dispatch(
        ListCoRoleName({
          name: "agent,admin,admin_with_billing",
          group_id: selectedGroupId,
        })
      );
      if (cancelled) return;
      if (response.payload?.status === 200 && Array.isArray(response.payload.data)) {
        const members = response.payload.data;
        setTeamMembers(members);
        setAssignedAgent((current) =>
          current && members.some((member) => member._id === current) ? current : ""
        );
      } else {
        setTeamMembers([]);
        setAssignedAgent("");
        toast.error("Failed to fetch team members. Please try again later.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, selectedGroupId]);

  useEffect(() => {
    setGroupLoading(true);
    dispatch(listAccessibleGroupsAPI()).then((res) => {
      if (res?.payload?.status === 200 && Array.isArray(res?.payload?.data)) {
        setGroupOptions(res.payload.data);
      }
      setGroupLoading(false);
    });
  }, [dispatch]);

  const colorCodesForAvatar = [
    '#405261', '#FF4D4F', '#FA541C', '#FA8C16', '#FAAD14', '#EB2F96', '#FADB14', '#A0D911', '#52C41A', '#5CDBD3', '#9254DE', '#F759AB', '#2F54EB', '#722ED1', '#1890FF', '#13C2C2', '#2DB7F5', '#19BE6B', '#30A745', '#FA8C16', '#FAAD14', '#9254DE', '#F759AB', '#13C2C2', '#722ED1', '#2DB7F5',
  ];
  const getAvatarColor = (title) => {
    if (!title) return colorCodesForAvatar[0];
    const first = title[0].toUpperCase();
    const idx = first.charCodeAt(0) - 65;
    return colorCodesForAvatar[(idx >= 0 && idx < colorCodesForAvatar.length) ? idx : 0];
  };

  const filteredGroups = groupSearchTerm
    ? groupOptions.filter((group) =>
      group.title.toLowerCase().includes(groupSearchTerm.toLowerCase())
    )
    : groupOptions;

  const handleDeleteGroup = (groupToDelete) => {
    setGroups(groups.filter((group) => group.id !== groupToDelete.id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const ticketData = {
      subject,
      status,
      priority,
      tags,
      chatRoomId: null,
      assignee: assignedAgent,
      business_id: userData?.response?.user?.business_id,
      customerEmail,
      customerName,
      description,
      groups: groups,
      custom_widget: true
    };

    const validationErrors = validateForm(ticketData);
    if (validationErrors.length > 0) {
      toast.error(validationErrors[0]);
      return;
    }
    setLoading(true);

    dispatch(createTicket(ticketData))
      .then((response) => {
        toast.success("Ticket created successfully!");
        console.log("Ticket created successfully!");

        // Emit socket event for real-time updates
        if (socket && response?.payload?.data) {
          socket.emit('create-ticket', {
            ticketId: response.payload.data._id,
            agentId: userData?.response?.user?._id,
            businessId: userData?.response?.user?.business_id,
            ticket: response.payload.data
          });
        }

        setSubject("");
        setStatus("");
        setPriority("");
        setCustomerEmail("");
        setCustomerName("");
        setAssignedAgent("");
        setTags([]);
        setDescription("");
        setGroups([]);
        setLoading(false);
        const created = response?.payload?.data ?? response?.payload;
        if (typeof onCreated === "function" && created) {
          onCreated(created);
        }
      })
      .catch(() => {
        toast.error("Failed to create ticket.");
        setLoading(false);
      });
  };
  return (
    <>

      <div className={`create-ticket-container${embedded ? " embedded" : ""}`}>
        <div className="field-wrapper">
          <TicketSubject subject={subject} setSubject={setSubject} />
        </div>
        <div style={{ display: "flex", gap: "8px", width: "100%" }}>
          <div className="field-wrapper">
            <Input
              className="customer-input"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              title="Customer's name"
              type="text"
              placeholder="Name"


            />
          </div>
          <div className="field-wrapper">
            <Input
              className="customer-input"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              size="small"
              title="Customer's email"
              placeholder="Email"
            />
          </div>
        </div>
        <TicketGroupSelector
          groups={groups}
          setGroups={setGroups}
          groupOptions={groupOptions}
          groupLoading={groupLoading}
          groupSearchTerm={groupSearchTerm}
          setGroupSearchTerm={setGroupSearchTerm}
          filteredGroups={filteredGroups}
          getAvatarColor={getAvatarColor}
        />
        {/* <div className="field-wrapper">
          <h3 className="field-label">Group</h3>
          <FormControl
            size="small"
            sx={{
              borderRadius: "8px",
              background: "#FFF",
              boxShadow: "0px 1px 2px rgba(6, 21, 34, 0.05)",
              fontSize: "14px",
              width: "100%",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #D5DCE1",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#148DFD",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#148DFD",
                borderWidth: 1,
              },
              "& .MuiSelect-icon": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                right: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiSelect-iconOpen": {
                fontSize: "16px",
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%) rotate(180deg)",
                right: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Select
              value={groups.length > 0 ? groups[0] : ""} // Single selection
              onChange={(e) => setGroups([e.target.value])} // Set as array with single item
              displayEmpty
              IconComponent={Dropdown}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <span
                      style={{
                        color: "#405261",
                        fontSize: "14px",
                        fontFamily: "Inter",
                        fontWeight: 400,
                      }}
                    >
                      Select a group
                    </span>
                  );
                }

                const selectedGroup = groupOptions.find(g => g.id === selected) ||
                  { title: typeof selected === 'string' ? selected : '' };

                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {selectedGroup.group_image ? (
                      <Avatar
                        src={selectedGroup.group_image}
                        alt={selectedGroup.title}
                        sx={{ width: 24, height: 24 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor: getAvatarColor(selectedGroup.title),
                          color: "#fff !important",
                          width: 24,
                          height: 24,
                          fontSize: "0.75rem",
                        }}
                      >
                        {selectedGroup.title?.[0]?.toUpperCase()}
                      </Avatar>
                    )}
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#061522",
                        fontFamily: "Inter",
                        fontWeight: 400,
                        textTransform: "capitalize",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                    >
                      {selectedGroup.title}
                    </span>
                  </Box>
                );
              }}
              onOpen={() => setGroupSearchTerm("")}
              sx={{
                height: "32px",
                minWidth: "128px",
                fontSize: "14px",
                color: "#405261",
                "& .MuiSelect-select": {
                  padding: "0px 16px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1px solid #E5E7EB",
                    marginTop: "8px",
                    borderRadius: "8px",
                    padding: "8px",
                    width: "280px",
                    maxHeight: "300px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    "& .MuiMenu-list": {
                      maxHeight: "260px",
                      overflowY: "auto",
                      padding: 0,
                      "&::-webkit-scrollbar": { display: "none" },
                    },
                    "& .MuiMenuItem-root": {
                      padding: '8px',
                      color: "#556575",
                      "&.Mui-selected": {
                        backgroundColor: "#F9FAFA !important",
                        borderRadius: "8px",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem
                disableRipple
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 1000,
                  p: "7px 0 !important",
                  "&:hover": { backgroundColor: "transparent !important" },
                }}
              >
                <TextField
                  fullWidth
                  autoFocus
                  variant="outlined"
                  placeholder="Search group"
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    sx: {
                      "& fieldset": { border: "1px solid #D5DCE1" },
                      "& input": {
                        height: "40px",
                        padding: "0px 16px",
                        fontSize: "14px",
                        "&::placeholder": {
                          color: "#C5CED6",
                          opacity: 1,
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": {
                        border: "1px solid #3B82F6",
                      },
                    },
                  }}
                />
              </MenuItem>

              {groupLoading && (
                <MenuItem disabled>
                  <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <CircularProgress size={20} />
                  </Box>
                </MenuItem>
              )}

              {(groupSearchTerm ? filteredGroups : groupOptions).map((group) => (
                <MenuItem
                  key={group.id}
                  value={group.id}
                  selected={groups.includes(group.id)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: '8px' }}>
                    <Avatar
                      sx={{
                        height: 32,
                        width: 32,
                        bgcolor: group.group_image ? "transparent" : getAvatarColor(group.title),
                      }}
                      src={group.group_image}
                    >
                      {!group.group_image && group.title.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ overflow: "hidden" }}>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#556575",
                          fontWeight: 500,
                          textTransform: "capitalize",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {group.title}
                      </div>
                    </Box>
                  </Box>
                </MenuItem>
              ))}

              {groupSearchTerm && filteredGroups.length === 0 && (
                <MenuItem disabled>
                  <Box
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      color: "#6B7280",
                      fontSize: "14px",
                      py: 1,
                    }}
                  >
                    No groups found
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </div> */}
        <div className="TicketTagInputContainer-Main">
          <div className="field-wrapper">
            <h3 className="field-label">Assign</h3>
            <TeamMemberSelect
              formControlSx={{
                width: "100%",
                height: "40px",
              }}
              selectSx={{

                "& .MuiSelect-select": {
                  padding: "0px 16px",
                },

              }}
              value={assignedAgent}
              onChange={(e) => setAssignedAgent(e.target.value)}
              teamMembers={teamMembers}
              placeholder={selectedGroupId ? "Assign" : "Select a group first"}
            />
          </div>
        </div>

        <TicketDropdowns
          status={status}
          setStatus={setStatus}
          priority={priority}
          setPriority={setPriority}
        />
        {/* <TicketDatePicker date={date} setDate={setDate} /> */}

        <TicketTagInput chipsContainerSx={{
          gap: "2px",




        }} allowHeightExpansion={true}
          tags={tags} setTags={setTags} returnIds={true} />

        <CreateTicketMessageContainer
          description={description}
          setDescription={setDescription}
          onSubmit={handleSubmit}
          loading={loading}
          onCancel={onCancel}
        />
      </div >
    </>
  );
}

export default CreateTicket;